'use client';

import { ServiceLayer } from '@packages/cms-components/src/modules/ServiceLayer/ServiceLayer';
import type { ArticleServiceFragmentDoc } from '@packages/gql/generated/shopping/ArticleServiceFragmentDoc';
import type { ArticleServicesCurrencyFragmentDoc } from '@packages/gql/generated/shopping/ArticleServicesCurrencyFragmentDoc';
import { type FragmentType } from '@packages/gql/generated/shopping/fragment-masking';
import { unmask } from '@packages/gql/src/betterMasking';
import { selectedServices } from '@packages/modules/src/atoms';
import { useAbTesting } from '@packages/shared/src/abtesting/useAbTesting/useAbTesting';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { Card } from '@packages/shared/src/ui/Card/Card';
import { Checkbox } from '@packages/shared/src/ui/Checkbox/Checkbox';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { DialogHeader } from '@packages/shared/src/ui/Dialog/DialogHeader/DialogHeader';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { usePriceFormater } from '../../../hooks/usePriceFormater';
import { articleServicesMessages } from '../../ArticleServices/messages';

export type ArticleServiceCardProps = {
  data: FragmentType<typeof ArticleServiceFragmentDoc>;
  /* should be in aPlus price value */
  currencyData: FragmentType<typeof ArticleServicesCurrencyFragmentDoc>;
};

export const ArticleServiceCard = ({ currencyData, data }: ArticleServiceCardProps) => {
  const {
    uuid,
    gratis,
    mandatory,
    serviceIcon,
    name,
    price,
    preselected,
    orderId,
    serviceLayerId,
  } = unmask<typeof ArticleServiceFragmentDoc>(data);
  const { currency } = unmask<typeof ArticleServicesCurrencyFragmentDoc>(currencyData);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selected, setSelected] = useState(Boolean(preselected));
  const setAtomSelected = useSetAtom(selectedServices);

  const { formatMessage } = useIntl();
  const formatPrice = usePriceFormater('APLUS');

  // can be removed when the test is finished
  const testId =
    process.env.NEXT_PUBLIC_ENVIRONMENT === 'staging'
      ? '2025_04_Aplus_reco_test'
      : '2025_04_Aplus_reco';

  useEffect(() => {
    // Update the selected state in the atom when the component mounts or when the selected state changes
    // If selected is true, add the service to the atom array
    // If selected is false, remove the service from the atom array, if service exists
    setAtomSelected((prev) =>
      selected
        ? prev.concat({ sku: orderId, selected: true })
        : prev.filter((service) => service.sku !== orderId),
    );
  }, [selected, setAtomSelected, orderId]);

  // remove if A/B test is finished
  const { setOutcome } = useAbTesting();

  const onClickHandler = () => {
    setSelected((prev) => !prev);
    setOutcome(testId, {
      COF: 1,
    });
  };

  return (
    <>
      <Card
        className={`relative flex h-full flex-col gap-1.5 border-2 px-1 pt-2 pb-1.5 select-none ${selected ? 'border-color-primary' : 'border-transparent'}`}
      >
        <div className="absolute top-0 right-0">
          {mandatory ? (
            <span className="icon-emp-checkmark text-color-success m-0.5 h-3 w-3" role="img" />
          ) : (
            <Checkbox
              checked={selected}
              onChange={onClickHandler}
              id={uuid}
              aria-checked={selected}
            />
          )}
        </div>

        <Image className="mx-auto" src={serviceIcon} width={56} height={56} alt="" />
        <p className="text-size-body2 grow">{name}</p>
        <div className="space-y-1.5">
          <button
            type="button"
            aria-haspopup="dialog"
            aria-label={formatMessage(articleServicesMessages.articleServicesMoreInfo)}
            className="text-size-body3 text-color-text-darktransparent text-left underline"
            onClick={() => setIsDialogOpen(true)}
          >
            {formatMessage(articleServicesMessages.articleServicesMoreInfo)}
          </button>

          {!gratis && price && (
            <p className="text-size-body2 leading-8 font-bold whitespace-nowrap">
              +{formatPrice(price, currency)}
            </p>
          )}
          {gratis && (
            <p className="text-size-body2 text-color-success-dark leading-8 font-bold whitespace-nowrap">
              {mandatory
                ? formatMessage(articleServicesMessages.articleServicesInclusive)
                : formatMessage(articleServicesMessages.articleServicesFree)}
            </p>
          )}
          <Button
            className={`w-full ${mandatory ? 'invisible' : ''}`}
            disabled={mandatory}
            onClick={onClickHandler}
          >
            {selected ? 'Entfernen' : 'Hinzufügen'}
          </Button>
        </div>
      </Card>
      <Dialog isOpen={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
        <DialogHeader onClose={() => setIsDialogOpen(false)}>{name || ''}</DialogHeader>
        <DialogContent>
          {isDialogOpen && <ServiceLayer serviceLayerId={serviceLayerId} />}
        </DialogContent>
      </Dialog>
    </>
  );
};
